import type { TaxProfileDto } from '@glory/contracts';
import { formatDate, formatMoney, t } from '@glory/i18n';
import { bpToDecimal, kopecksFromRubles, roundHalfUpToKopeck } from '@glory/kernel';
import { TAX_ENTITY, TAX_ENTITY_ID } from '@glory/pricing/domain';
import {
  Button,
  Callout,
  Card,
  ContentSwap,
  Dialog,
  EmptyState,
  InfoHint,
  PageHeader,
  SettingsSection,
  SkeletonRows,
} from '@glory/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Landmark, Plus } from 'lucide-react';
import { useState } from 'react';

import {
  formatTaxValue,
  TAX_FIELD_LABEL,
  TAX_FIELDS,
  taxChanges,
  taxFieldsOf,
  taxSummary,
  versionBefore,
} from '../../../features/pricing/model.ts';
import { TaxDialog, type TaxDialogMode } from '../../../features/pricing/tax-dialog.tsx';
import { CollapsedHistory, ScheduledVersion, VersionList } from '../../../features/pricing/version-parts.tsx';
import { client, orpc } from '../../../lib/orpc.ts';
import { requireSettingsPermission, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/settings/tax')({
  beforeLoad: requireSettingsPermission('pricing.read'),
  component: TaxPage,
});

const HUNDRED_THOUSAND = kopecksFromRubles(100_000);

function TaxPage() {
  const { can } = usePermissions();
  const canRead = can('pricing.read');
  const canManage = can('pricing.manage');
  const list = useQuery({ ...orpc.pricing.tax.list.queryOptions(), enabled: canRead });
  const [dialog, setDialog] = useState<TaxDialogMode | null>(null);
  const profiles = list.data?.items ?? [];
  const current = profiles.find((p) => p.status === 'current') ?? null;
  const scheduled = profiles.filter((p) => p.status === 'scheduled').reverse();
  const open = (mode: TaxDialogMode) => {
    setDialog(mode);
  };
  // Загрузка, сбой, пустое состояние и профиль проявляются друг из друга, без скачка высоты.
  const phase = !canRead
    ? 'denied'
    : list.isError
      ? 'error'
      : !list.data
        ? 'loading'
        : current
          ? 'ready'
          : 'empty';

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 whitespace-normal">
            {t('tax.title')}
            <InfoHint hint={{ text: t('tax.hint'), example: t('tax.hintExample') }} />
          </span>
        }
      />
      <p className="-mt-2 text-muted">{t('tax.lead')}</p>

      <ContentSwap swapKey={phase} contentClassName="flex flex-col gap-4">
        {!canRead ? (
          <Callout tone="strong">{t('tax.noAccess')}</Callout>
        ) : list.isError ? (
          <Callout>{list.error.message}</Callout>
        ) : !list.data ? (
          <SkeletonRows rows={6} />
        ) : (
          <>
            {current ? (
              <SettingsSection
                title={t('tax.profileTitle')}
                lead={t('tax.since', { date: formatDate(current.effectiveFrom) })}
                action={
                  canManage ? (
                    <Button
                      onClick={() => {
                        open({ kind: 'new' });
                      }}
                    >
                      <Plus /> {t('tax.newProfile')}
                    </Button>
                  ) : null
                }
              >
                <CurrentProfile profile={current} />
                {scheduled.length > 0 ? (
                  <div className="mt-3 flex flex-col gap-2">
                    {scheduled.map((profile) => (
                      <ScheduledProfile
                        key={profile.id}
                        profile={profile}
                        profiles={profiles}
                        canManage={canManage}
                        onEdit={open}
                      />
                    ))}
                  </div>
                ) : null}
              </SettingsSection>
            ) : (
              <Card>
                <EmptyState
                  icon={<Landmark />}
                  title={t('tax.emptyTitle')}
                  description={canManage ? t('tax.emptyText') : t('tax.emptyReadOnly')}
                  action={
                    canManage ? (
                      <Button
                        variant="primary"
                        onClick={() => {
                          open({ kind: 'new' });
                        }}
                      >
                        {t('tax.create')}
                      </Button>
                    ) : null
                  }
                />
                {scheduled.length > 0 ? (
                  <div className="flex flex-col gap-2 border-t border-border-soft px-4 py-3">
                    {scheduled.map((profile) => (
                      <ScheduledProfile
                        key={profile.id}
                        profile={profile}
                        profiles={profiles}
                        canManage={canManage}
                        onEdit={open}
                      />
                    ))}
                  </div>
                ) : null}
              </Card>
            )}
            {profiles.length > 0 ? (
              <VersionList
                title={t('tax.versionsTitle')}
                lead={t('tax.versionsLead')}
                rows={profiles.map((profile) => ({ ...profile, summary: taxSummary(profile) }))}
                scheduledLabel={t('tax.statusScheduled')}
              />
            ) : null}
            <CollapsedHistory
              title={t('tax.historyTitle')}
              lead={t('tax.historyLead')}
              entityType={TAX_ENTITY}
              entityId={TAX_ENTITY_ID}
            />
          </>
        )}
      </ContentSwap>

      <Dialog
        open={dialog !== null}
        onOpenChange={(next) => {
          if (!next) setDialog(null);
        }}
      >
        {dialog && list.data ? (
          <TaxDialog
            mode={dialog}
            profiles={profiles}
            today={list.data.today}
            onDone={() => {
              setDialog(null);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function CurrentProfile({ profile }: { profile: TaxProfileDto }) {
  const fields = TAX_FIELDS.filter((field) => taxFieldsOf(profile).includes(field));
  return (
    <div className="flex flex-col gap-2">
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {fields.map((field) => (
          <div
            key={field}
            className={
              field === 'incomeKind'
                ? 'col-span-2 rounded-md bg-surface-2 px-2.5 py-2'
                : 'rounded-md bg-surface-2 px-2.5 py-2'
            }
          >
            <dt className="text-caption font-semibold text-muted">{TAX_FIELD_LABEL[field]}</dt>
            <dd className="font-display text-title font-bold tabular-nums">
              {formatTaxValue(field, profile)}
            </dd>
          </div>
        ))}
      </dl>
      {profile.incomeKind === 'usn_income' ? (
        <p className="text-small text-muted">
          {t('tax.example', {
            amount: formatMoney(roundHalfUpToKopeck(bpToDecimal(profile.incomeRate).times(HUNDRED_THOUSAND))),
          })}
        </p>
      ) : (
        <p className="text-small text-muted">{t('tax.approximation')}</p>
      )}
      {profile.comment ? <p className="text-small text-muted">«{profile.comment}»</p> : null}
    </div>
  );
}

function ScheduledProfile({
  profile,
  profiles,
  canManage,
  onEdit,
}: {
  profile: TaxProfileDto;
  profiles: readonly TaxProfileDto[];
  canManage: boolean;
  onEdit: (mode: TaxDialogMode) => void;
}) {
  const queryClient = useQueryClient();
  const previous = versionBefore(profiles, profile.effectiveFrom);
  return (
    <ScheduledVersion
      title={t('tax.scheduledTitle', { date: formatDate(profile.effectiveFrom) })}
      changes={taxChanges(previous, profile)}
      hasPrevious={previous !== null}
      sameText={t('tax.scheduledSame')}
      comment={profile.comment}
      canManage={canManage}
      onEdit={() => {
        onEdit({ kind: 'scheduled', profile });
      }}
      onRemove={async () => {
        await client.pricing.tax.remove({ id: profile.id });
        await queryClient.invalidateQueries({ queryKey: orpc.pricing.key() });
      }}
      removedText={t('tax.scheduledRemoved', { date: formatDate(profile.effectiveFrom) })}
    />
  );
}
